/**
 * SpecialistID — Product PDP modular bundle: PRICING (MAP / MSRP)
 * Part of the custom-product-* set (split out of the legacy custom-product.js).
 *
 * Owns the price display logic: MAP / MSRP / compare-at / "Add to Cart for
 * Price" states, the reward-points value, and the total-price line. Also hosts
 * the FPD price observer, which re-runs the pricing logic when the FPD designer
 * updates the hidden price field.
 *
 * Public API: SPID.mapMsrpLogic(variant_id, price) — also exported as
 * window.mapMsrpLogic for the external FPD designer integration.
 *
 * Cross-module: calls SPID.updateTablePrice() (volume-pricing logic, currently
 * still in custom-product.js) dynamically at runtime, so it resolves once that
 * file has loaded regardless of script order.
 *
 * LOAD ORDER: after custom-product-core.js + custom-product-data.js, before
 * custom-product.js.
 */
(function () {
  'use strict';

  const SPID = window.SPID;
  const debugLog = SPID.debugLog;
  const formatCurrency = SPID.formatCurrency;
  // One markup for the unit price everywhere it is written (Liquid, this file, the volume
  // table, the price ledger): amount, then a small gray "ea" (2026-10-09).
  // A sealed pack (SPID.packQty > 1, set per variant by mapMsrpLogic) reads "/ 25-pack" instead.
  const priceHTML = function (amount) {
    return formatCurrency(amount) + '<span class="price-ea">' + (SPID.packQty > 1 ? '/ ' + SPID.packQty + '-pack' : 'ea') + '</span>';
  };
  SPID.priceHTML = priceHTML;

  // Pack price (Patrick, 2026-10-19): main-product.liquid puts {variantId: pieces} on .actual-price
  // (data-packs="variantId:pieces,...") for products sold in sealed packs, from the native unit
  // price measurement.
  SPID.packQty = 0;
  let packMap = null;
  const packQtyFor = function (variant_id) {
    if (packMap === null) {
      const el = document.querySelector('.actual-price[data-packs]');
      packMap = {};
      (el ? el.getAttribute('data-packs') : '').split(',').forEach(function (pair) {
        const kv = pair.split(':');
        if (kv.length === 2) packMap[kv[0]] = kv[1];
      });
    }
    return parseInt(packMap[String(variant_id)]) || 0;
  };
  // The reserved "$0.54 ea" line under a pack price; empty for a single-piece variant.
  const paintPackEach = function (amount) {
    const el = document.querySelector('.actual-price > .price-pack-ea');
    if (!el) return;
    const t = SPID.packQty > 1 ? formatCurrency(amount / SPID.packQty) + ' ea' : '';
    if (el.textContent !== t) el.textContent = t;
  };

  // The price block (Patrick, 2026-10-09, revision 2):
  //   .price-was   small struck compare-at price ABOVE the price - only for a real compare-at
  //   .product-price  the unit price + "ea"
  //   .price-save  small "Save N%" BELOW the price - from the compare-at price when there is
  //                one, otherwise from the starting tier price (no struck price for tiers)
  // main-product.liquid reserves both lines on every product that can ever fill them, so a
  // tier or variant click only changes text and nothing moves. One writer for every page that
  // shows a tiered price; a repeat call with the same numbers writes nothing.
  SPID.compareAt = 0;
  SPID.paintPriceSave = function (unit, start) {
    var pp = document.querySelector('.actual-price .product-price');
    if (!pp || !pp.parentNode) return;
    var box = pp.parentNode;
    var cmp = SPID.compareAt;
    var ref = cmp > start ? cmp : start;
    var pct = ref > 0 && unit > 0 && unit < ref ? Math.floor((ref - unit) / ref * 100) : 0;
    var wasTxt = cmp > 0 ? formatCurrency(cmp) : '';
    var saveTxt = pct > 0 ? 'Save ' + pct + '%' : '';
    var line = function (cls, txt, before) {
      var el = box.querySelector(':scope > .' + cls);
      if (!el) {
        if (!txt) return;
        el = document.createElement('div');
        el.className = cls;
        box.insertBefore(el, before ? pp : pp.nextSibling);
      }
      if (el.textContent.trim() !== txt) el.innerHTML = txt && cls === 'price-was' ? '<s>' + txt + '</s>' : txt;
    };
    line('price-was', wasTxt, true);
    line('price-save', saveTxt, false);
  };
  const getVariantId = SPID.getVariantId;
  const getVariantPrice = SPID.getVariantPrice;
  const getVariantCompareAtPrice = SPID.getVariantCompareAtPrice;
  const getVariantMetafield = SPID.getVariantMetafield;
  const getProductMetafield = SPID.getProductMetafield;

  /* ---------- MAP / MAPP guard (2026-07-28, Patrick) ----------
     Single source of truth for "may we print this variant's price?", shared by
     mapMsrpLogic() and updateTablePrice(). Without the sharing, the volume
     pricing table happily overwrote the "Add to Cart for Price" line that
     mapMsrpLogic had just put in .actual-price .product-price.

     MAP is the source of truth and the ONLY trigger (Patrick, 2026-07-28):
       1. c_f.map (variant metafield first, product fallback). Where a MAP
          exists it decides on its own -- hide the number ONLY when the MAP
          is strictly ABOVE the selling price. A price equal to (or above)
          MAP is already compliant and needs no treatment.
       2. the hide_searchanise_price tag, surfaced by main-product.liquid as
          metafields.custom.hide_price. Demoted to a fallback for products
          with no c_f.map value at all; it can no longer override a known MAP.
     c_f.map is in DOLLARS; getVariantPrice() already returns dollars. */
  function getMapValue(variantId) {
    let map = parseFloat(getVariantMetafield(variantId, 'c_f', 'map')) || 0;
    if (map === 0) map = parseFloat(getProductMetafield('c_f', 'map')) || 0;
    return map;
  }

  function isPriceHidden(variantId, priceInDollars) {
    const map = getMapValue(variantId);
    const p = Number(priceInDollars == null ? getVariantPrice(variantId) : priceInDollars);
    // A usable MAP settles it by itself, equal case included.
    if (map > 0 && isFinite(p) && p > 0) return p < map;
    // No MAP to evaluate -- fall back to the tag.
    return getProductMetafield('custom', 'hide_price') === true;
  }

  function mapMsrpLogic(variant_id, price) {
    if (!variant_id) {
      variant_id = getVariantId();
      if (!variant_id) return;
    }

    // Use helper function to get price
    if (!price || price === 0) {
      // For FPD products, check the hidden price field first (set by FPD designer)
      if (document.body.classList.contains('fpd-enabled')) {
        const fpdPriceEl = document.querySelector(".variant_price");
        if (fpdPriceEl && fpdPriceEl.value) {
          price = parseFloat(fpdPriceEl.value);
        }
      }
      // Fall back to variant price if FPD price not available
      if (!price || price === 0) {
        price = getVariantPrice(variant_id);
      }
    }

    // Exit if price is still 0 and FPD is enabled (price will be set by FPD)
    if ((!price || price === 0) && document.body.classList.contains('fpd-enabled')) {
      return;
    }

    const isDiscontinued = getProductMetafield('status', 'discontinued');
    if (isDiscontinued && isDiscontinued !== 'false' && isDiscontinued !== false) {
      const priceDisplay = document.querySelector(".product-price-display");
      if (priceDisplay) priceDisplay.innerHTML = '';
      const perItem = document.querySelector(".per-item-price");
      if (perItem) perItem.style.display = 'none';
      return;
    }

    var qty = parseInt(document.querySelector(".quantity__input")?.value) || 1;
    const rewardPoints = document.querySelector('div.reward_points > span');
    var price_num = parseFloat(price);
    // A page that prices by quantity tier (the price ledger in the custom, credential and
    // UV designers) registers SPID.tierUnit(qty) -> unit price in dollars. The price and
    // total are written at that unit HERE, so the ledger's repaint finds nothing to change
    // and the buy box never shows the base price or a different layout for a frame.
    // MAP / MSRP / compare-at decisions still use the variant's own price.
    var show_num = price_num;
    SPID.packQty = packQtyFor(variant_id);
    if (typeof SPID.tierUnit === 'function') {
      var tierUnit = SPID.tierUnit(qty);
      if (tierUnit > 0) show_num = tierUnit;
    }
    
    // Get pricing data using helper functions
    var compare_at_price_num = getVariantCompareAtPrice(variant_id);
    
    // Get MAP and MSRP values from metafields only
    var map_value_num = parseFloat(getVariantMetafield(variant_id, 'c_f', 'map')) || 0;
    var msrp_value_num = parseFloat(getVariantMetafield(variant_id, 'c_f', 'msrp')) || 0;

    // Product-level fallback (if variant doesn't have it)
    if (map_value_num === 0) {
      map_value_num = parseFloat(getProductMetafield('c_f', 'map')) || 0;
    }

    if (msrp_value_num === 0) {
      msrp_value_num = parseFloat(getProductMetafield('c_f', 'msrp')) || 0;
    }

    debugLog(`mapMsrpLogic for variant ${variant_id}: map=${map_value_num}, msrp=${msrp_value_num}`);

    var msrp_txt = '';
    var map_txt = '';
    var totalPrice = show_num * qty;

    /* MAP / MAPP (2026-07-28) -- one shared rule, see isPriceHidden(). The volume
       pricing table uses the identical call so the two cannot disagree. */
    const belowMap = isPriceHidden(variant_id, price_num);
    SPID.compareAt = (!belowMap && compare_at_price_num > price_num) ? compare_at_price_num : 0;

    if (belowMap) {
      /* The struck-through MAP is only printable while it sits ABOVE the selling
         price. map_value_num is 0 when only the tag fired (no c_f.map), and 63
         tagged products are priced exactly AT their MAP -- printing the MAP
         there would print the very number we were told to withhold.
         See snippets/price.liquid for the full note. */
      msrp_txt = map_value_num > price_num
        ? '<div class="regios-dopp-generic-price-item--regular">Regular price: <s><span id="map-price">' + formatCurrency(map_value_num) + '</span></s></div>'
        : '';
      map_txt = '<div class="product-price regios-dopp-generic-price-item--sale">Add to Cart for Price</div>';
      const rewardDiv = document.querySelector("div.reward_points");
      if (rewardDiv) rewardDiv.classList.add("hide");
    } else if (price_num >= map_value_num && price_num < msrp_value_num) {
      msrp_txt = '<div class="regios-dopp-generic-price-item--regular">MSRP: <s><span id="map-price">' + formatCurrency(msrp_value_num) + '</span></s></div>';
      map_txt = '<div class="product-price regios-dopp-generic-price-item--sale">' + priceHTML(show_num) + '</div>';
      const rewardDiv = document.querySelector("div.reward_points");
      if (rewardDiv) rewardDiv.classList.add("hide");
    } else if (price_num >= map_value_num && price_num >= msrp_value_num) {
      msrp_txt = '';
      map_txt = '<div class="product-price regios-dopp-generic-price-item--sale">' + priceHTML(show_num) + '</div>';
      const rewardDiv = document.querySelector("div.reward_points");
      if (rewardDiv) {
        rewardDiv.classList.remove("hide");
        if (rewardPoints) rewardPoints.innerHTML = totalPrice.toFixed(2);
      }
    } else {
      map_txt = '<div class="product-price regios-dopp-generic-price-item--sale">' + priceHTML(show_num) + '</div>';
      const rewardDiv = document.querySelector("div.reward_points");
      if (rewardDiv) {
        rewardDiv.classList.remove("hide");
        if (rewardPoints) rewardPoints.innerHTML = totalPrice.toFixed(2);
      }
    }
    
    /* MAP outranks the compare-at treatment. Before 2026-07-28 this block ran
       unconditionally and overwrote map_txt, so any MAP-restricted product that
       also carried a compare_at_price printed its real selling price -- the same
       branch-order bug that was fixed in snippets/card-product.liquid. */
    if (compare_at_price_num > 0 && compare_at_price_num > price_num && !belowMap) {
      // The list price is struck above the price by SPID.paintPriceSave below.
      msrp_txt = '';
      totalPrice = show_num * qty;
      map_txt = '<div class="product-price regios-dopp-generic-price-item--sale">' + priceHTML(show_num) + '</div>';
      const rewardDiv = document.querySelector("div.reward_points");
      if (rewardDiv) {
        rewardDiv.classList.remove("hide");
        if (rewardPoints) rewardPoints.innerHTML = totalPrice.toFixed(2);
      }
    }

    // A compare-at price alone keeps the server-rendered block (was line / price / save line)
    // and only rewrites the price; SPID.paintPriceSave below fills the two small lines.
    if (belowMap
      || (price_num >= map_value_num && price_num < msrp_value_num)) {
      if (belowMap
      || (price_num >= map_value_num && price_num < msrp_value_num)) {
        const wrapper = document.querySelector("div.total-price-wrapper");
        if (wrapper) wrapper.classList.add("hidden");
      }
      
      const actualPrice = document.querySelector(".actual-price");
      if (actualPrice) actualPrice.innerHTML = msrp_txt + map_txt;
    } else {
      const wrapper = document.querySelector("div.total-price-wrapper");
      if (wrapper) wrapper.classList.remove("hidden");
      // Clear any stale "List Price: $X ea" line left over from a previous
      // variant that triggered the MAP/MSRP/compare-at branch above. Without
      // this, switching from an on-sale variant to a non-sale variant leaves
      // the previous variant's compare-at value displayed (orphaned).
      const listPriceEl = document.querySelector(".actual-price .regios-dopp-generic-price-item--regular");
      if (listPriceEl) {
        listPriceEl.innerHTML = '';
        listPriceEl.classList.add('visually-hidden');
      }
      const productPrice = document.querySelector(".product-price");
      if (productPrice) productPrice.innerHTML = priceHTML(show_num);
    }
    
    paintPackEach(show_num);

    const totalPriceEl = document.querySelector(".total-price-wrapper .total-price");
    if (totalPriceEl) totalPriceEl.innerHTML = formatCurrency(totalPrice + (typeof SPID.addonUnit === 'function' ? SPID.addonUnit() : 0) * qty);

    // SPID-qualified: this file has no local binding for updateTablePrice, which
    // lives in custom-product-volume-pricing.js. The monolith could call it bare
    // because both sat in one IIFE; across the module split it must go through
    // the namespace. Calling it bare threw ReferenceError here and silently
    // skipped the per-item-price toggle below.
    SPID.call('updateTablePrice');

    if (!belowMap) {
      var minQ = parseInt(document.querySelector('.quantity__input')?.getAttribute('min'), 10) || 1;
      var startNum = typeof SPID.tierUnit === 'function' ? (SPID.tierUnit(minQ) || price_num) : price_num;
      SPID.paintPriceSave(show_num, startNum);
    }

    if (qty === 1) {
      const perItem = document.querySelector(".per-item-price");
      if (perItem) perItem.style.display = 'none';
    } else {
      const perItem = document.querySelector(".per-item-price");
      if (perItem) perItem.style.display = 'block';
    }
  }

  // Expose mapMsrpLogic for cross-module use (custom-product.js) and globally
  // for the external FPD designer integration.
  SPID.mapMsrpLogic = mapMsrpLogic;
  // Shared MAP/MAPP guard — the volume-pricing module calls the identical
  // isPriceHidden() so the buy box and the tier table cannot disagree.
  SPID.getMapValue = getMapValue;
  SPID.isPriceHidden = isPriceHidden;
  window.mapMsrpLogic = mapMsrpLogic;

  // Watch price element for changes and correct if it doesn't match FPD price
  function setupFpdPriceObserver() {
    const priceElement = document.querySelector('.product-price.regios-dopp-generic-price-item--sale');
    if (!priceElement) return;

    let isUpdating = false;
    const observer = new MutationObserver(function() {
      if (isUpdating) return;

      isUpdating = true;
      mapMsrpLogic();
      setTimeout(function() { isUpdating = false; }, 100);
    });

    observer.observe(priceElement, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  // Initialize FPD price observer when fpd-enabled class is added to body
  function waitForFpdEnabled(callback) {
    if (document.body.classList.contains('fpd-enabled')) {
      callback();
      return;
    }
    // Watch for fpd-enabled class to be added to body
    const bodyObserver = new MutationObserver(function() {
      if (document.body.classList.contains('fpd-enabled')) {
        bodyObserver.disconnect();
        callback();
      }
    });
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  document.addEventListener('DOMContentLoaded', function() {
    waitForFpdEnabled(setupFpdPriceObserver);
  });

  // Announce this module so main-product.liquid can leave it out safely.
  if (window.SPID && SPID.register) SPID.register('pricing');
})();
